import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  TextField,
  Select,
  MenuItem,
  Button,
  Tabs,
  Tab, 
  Autocomplete
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ChromePicker } from 'react-color';
import { useTheme as useAppTheme } from '../../../context/ThemeContext';
import { useApi } from 'context/ApiProvider';
import MiniViewer3D, { CaptureImageOptions } from "../../MiniViewer3D/MiniViewer3D";
import { useSelectedDesignStore } from "context/SelectedDesignContext";
import toast from 'react-hot-toast';
import { takePendingSaveTemplateFields, claimVoiceTemplateSave, releaseVoiceTemplateSave, SaveTemplateVoiceFields } from "Voice/voiceCommands";


interface SaveTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const WEB_TEMPLATE_COLORS = [
  { name: 'DodgerBlue', value: '#1E90FF' },
  { name: 'SlateGray', value: '#708090' },
  { name: 'LightSlateGray', value: '#778899' },
  { name: 'LightSteelBlue', value: '#B0C4DE' },
  { name: 'CornflowerBlue', value: '#6495ED' },
  { name: 'RoyalBlue', value: '#4169E1' },
  { name: 'MidnightBlue', value: '#191970' },
  { name: 'Lavender', value: '#E6E6FA' },
  { name: 'Navy', value: '#000080' },
  { name: 'SkyBlue', value: '#87CEEB' },
  { name: 'SteelBlue', value: '#4682B4' },
  { name: 'LightSkyBlue', value: '#87CEFA' },
  { name: 'SlateBlue', value: '#6A5ACD' },
  { name: 'MediumSlateBlue', value: '#7B68EE' },
  { name: 'MediumBlue', value: '#0000CD' },
  { name: 'DeepSkyBlue', value: '#00BFFF' },
];

const SYSTEM_TEMPLATE_COLORS = [
  { name: 'Red', value: '#FF0000' },
  { name: 'Green', value: '#008000' },
  { name: 'Blue', value: '#0000FF' },
  { name: 'Yellow', value: '#FFFF00' },
  { name: 'Cyan', value: '#00FFFF' },
  { name: 'Magenta', value: '#FF00FF' },
  { name: 'Orange', value: '#FFA500' },
  { name: 'Purple', value: '#800080' },
  { name: 'Brown', value: '#A52A2A' },
  { name: 'Black', value: '#000000' },
  { name: 'White', value: '#FFFFFF' },
  { name: 'Gray', value: '#808080' },
  { name: 'DarkRed', value: '#8B0000' },
  { name: 'DarkGreen', value: '#006400' },
  { name: 'DarkBlue', value: '#00008B' },
];

function resolveTemplateColor(raw?: string): { tab: string; hex: string } | null {
  if (!raw?.trim()) return null;
  const trimmed = raw.trim();
  if (/^#?[0-9a-fA-F]{6}$/.test(trimmed)) {
    const hex = (trimmed.startsWith("#") ? trimmed : `#${trimmed}`).toUpperCase();
    return { tab: "custom", hex };
  }
  const compact = trimmed.toLowerCase().replace(/[\s_-]/g, "");
  const system = SYSTEM_TEMPLATE_COLORS.find((item) => item.name.toLowerCase().replace(/[\s_-]/g, "") === compact);
  if (system) return { tab: "system", hex: system.value };
  const web = WEB_TEMPLATE_COLORS.find((item) => item.name.toLowerCase().replace(/[\s_-]/g, "") === compact);
  if (web) return { tab: "web", hex: web.value };
  return null;
}

interface MiniViewer3DHandle {
  captureImage: (options?: CaptureImageOptions) => string | null;
  updateMaterialColor: (color: string, target: 'insole' | 'additions') => void;
}

const SaveTemplateModal: React.FC<SaveTemplateModalProps> = ({ isOpen, onClose }) => {
  const { isDarkMode } = useAppTheme();
  const designId = useSelectedDesignStore((state) => state.designId);
  const api = useApi();
  const defaultInsoleValue = 'web:';
  const defaultAdditionValue = 'web:';
  const parseValue = (val: string) => {
  if (!val || !val.includes(':')) {
    return { tab: 'custom', color: '' }; 
  }
  const [tab, color] = val.split(':');
  return { tab, color };
  };
  const [templateTitle, setTemplateTitle] = useState('');
  const [categoryOptions, setCategoryOptions] = useState<string[]>([
  "default",
  ]);
  const [category, setCategory] = useState<string | null>(null);
  const [insoleValue, setInsoleValue] = useState(defaultInsoleValue);
  const [additionValue, setAdditionValue] = useState(defaultAdditionValue);
  const [tempInsoleColor, setTempInsoleColor] = useState(parseValue(defaultInsoleValue).color);
  const [tempAdditionColor, setTempAdditionColor] = useState(parseValue(defaultAdditionValue).color);
  const [insoleTabIndex, setInsoleTabIndex] = useState(1);  
  const [additionTabIndex, setAdditionTabIndex] = useState(1);

  const [insoleSelectOpen, setInsoleSelectOpen] = useState(false);
  const [additionSelectOpen, setAdditionSelectOpen] = useState(false);

  const miniViewerRef = useRef<MiniViewer3DHandle>(null);
  const insoleSelectRef = useRef<HTMLDivElement>(null);
  const insoleMenuRef = useRef<HTMLDivElement>(null);
  const additionSelectRef = useRef<HTMLDivElement>(null);
  const additionMenuRef = useRef<HTMLDivElement>(null);

const handleClickOutsideInsole = (event: MouseEvent) => {
  if (
    insoleSelectRef.current &&
    insoleMenuRef.current &&
    !insoleSelectRef.current.contains(event.target as Node) &&
    !insoleMenuRef.current.contains(event.target as Node)
  ) {
    setInsoleSelectOpen(false);
  }
};

const handleClickOutsideAddition = (event: MouseEvent) => {
  if (
    additionSelectRef.current &&
    additionMenuRef.current &&
    !additionSelectRef.current.contains(event.target as Node) &&
    !additionMenuRef.current.contains(event.target as Node)
  ) {
    setAdditionSelectOpen(false);
  }
};

useEffect(() => {
  if (insoleSelectOpen) {
    document.addEventListener("mousedown", handleClickOutsideInsole);
  } else {
    document.removeEventListener("mousedown", handleClickOutsideInsole);
  }
  return () => {
    document.removeEventListener("mousedown", handleClickOutsideInsole);
  };
}, [insoleSelectOpen]);

useEffect(() => {
  if (additionSelectOpen) {
    document.addEventListener("mousedown", handleClickOutsideAddition);
  } else {
    document.removeEventListener("mousedown", handleClickOutsideAddition);
  }
  return () => {
    document.removeEventListener("mousedown", handleClickOutsideAddition);
  };
}, [additionSelectOpen]);

  const handleClose = () => {
  setInsoleValue(defaultInsoleValue);
  setAdditionValue(defaultAdditionValue);
  setTemplateTitle('');
  setCategory('');
  onClose();
  };

  const titleRef = useRef(templateTitle);
  const categoryRef = useRef(category);
  titleRef.current = templateTitle;
  categoryRef.current = category;

 const handleSave = async (overrides?: SaveTemplateVoiceFields) => {
  const title = (overrides?.name || titleRef.current || "").trim();
  if (!title) {
    toast.error("Template title is required.");
    return false;
  }
  const imageDataUrl = miniViewerRef.current?.captureImage({
    mimeType: 'image/jpeg',
    quality: 0.9,
    maxSizeBytes: 2 * 1024 * 1024,
    maxWidth: 1200,
    maxHeight: 1200,
  });
  if (!imageDataUrl) {
    toast.error("Unable to capture template image.");
    return false;
  }
  const blob = await fetch(imageDataUrl).then(res => res.blob());

  const formData = new FormData();
  formData.append("Name", title);
  formData.append("Category", (overrides?.category || categoryRef.current || "default").toString());
  formData.append("Image", blob, "thumbnail.jpg");
  const res = await api.post(`/api/templates/${designId}`, formData);
  if (res.ok) toast.success(`New template were saved successfully.`, {duration: 5000 });
  else toast.error(`Failed to save new template. Please try again.`, { duration: 10000 });
  handleClose();
  return res.ok;
};

  const applyVoiceFields = (detail?: SaveTemplateVoiceFields) => {
    const title = detail?.name?.trim();
    const cat = detail?.category?.trim();
    if (title) {
      setTemplateTitle(title);
      titleRef.current = title;
    }
    if (cat) {
      setCategory(cat);
      categoryRef.current = cat;
      setCategoryOptions((prev) => (prev.includes(cat) ? prev : [...prev, cat]));
    }
    const insole = resolveTemplateColor(detail?.insoleColor);
    if (insole) {
      setInsoleValue(`${insole.tab}:${insole.hex}`);
      setTempInsoleColor(insole.hex);
      miniViewerRef.current?.updateMaterialColor(insole.hex, "insole");
    }
    const addition = resolveTemplateColor(detail?.additionColor);
    if (addition) {
      setAdditionValue(`${addition.tab}:${addition.hex}`);
      setTempAdditionColor(addition.hex);
      miniViewerRef.current?.updateMaterialColor(addition.hex, "additions");
    }
  };

  const commitVoiceSave = (detail?: SaveTemplateVoiceFields) => {
    const title = (detail?.name || titleRef.current || "").trim();
    if (!claimVoiceTemplateSave(title)) return;
    window.setTimeout(async () => {
      try {
        await handleSave(detail);
      } finally {
        releaseVoiceTemplateSave();
      }
    }, 220);
  };

  useEffect(() => {
    const onVoiceSaveTemplate = (event: Event) => {
      const detail = (event as CustomEvent<SaveTemplateVoiceFields>).detail;
      applyVoiceFields(detail);
      if (!isOpen) return;
      takePendingSaveTemplateFields();
      if (detail?.save) commitVoiceSave(detail);
    };
    document.addEventListener("voice:openSaveTemplate", onVoiceSaveTemplate as EventListener);
    return () => document.removeEventListener("voice:openSaveTemplate", onVoiceSaveTemplate as EventListener);
  }, [isOpen]);

useEffect(() => {
  if (isOpen) {
    const pending = takePendingSaveTemplateFields();
    applyVoiceFields(pending);
    if (pending.save) commitVoiceSave(pending);
    const fetchCategories = async () => {
      try {
        const res = await api.get('/api/templates');
        if (res.ok && Array.isArray(res.body)) {
          const data = res.body as Array<{ category?: string }>;
          const categories: string[] = data
            .map(t => t.category?.trim() ?? '')
            .filter((cat): cat is string => cat.length > 0);

          const uniqueCategories: string[] = Array.from(new Set(categories));
          if (!uniqueCategories.includes("default")) {
            uniqueCategories.unshift("default");
          }
          setCategoryOptions(uniqueCategories);
        } else {
          console.error('Failed to fetch templates or invalid data');
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    };
    fetchCategories();
  }
  
  if (miniViewerRef.current) {
    const { color: insoleColor } = parseValue(insoleValue ?? '');
    const { color: additionColor } = parseValue(additionValue ?? '');

    if (insoleColor) {
      miniViewerRef.current.updateMaterialColor(insoleColor, 'insole');
    }
    if (additionColor) {
      miniViewerRef.current.updateMaterialColor(additionColor, 'additions');
    }
  }
  {
    const { tab } = parseValue(insoleValue);
    if (tab === 'custom') setInsoleTabIndex(0);
    else if (tab === 'web') setInsoleTabIndex(1);
    else if (tab === 'system') setInsoleTabIndex(2);
    else setInsoleTabIndex(1);
  }
  {
    const { tab } = parseValue(additionValue);
    if (tab === 'custom') setAdditionTabIndex(0);
    else if (tab === 'web') setAdditionTabIndex(1);
    else if (tab === 'system') setAdditionTabIndex(2);
    else setAdditionTabIndex(1);
  }

}, [isOpen, insoleValue, additionValue]);


  const webColors = WEB_TEMPLATE_COLORS;
  const systemColors = SYSTEM_TEMPLATE_COLORS;

const getColorName = (list: { name: string; value: string }[], value: string) => {
  const found = list.find(c => c.value.toLowerCase() === value.toLowerCase());
  return found ? found.name : value;
};

const renderColorPicker = (type: number, color: string, setColor: (c: string) => void, pickerType: "insole" | "addition") => {
  if (type === 0) {
    return (
    <Box sx={{ width: '100%',
      maxWidth: 410,
      height: 370,
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      boxSizing: 'border-box',}}>
      <ChromePicker
      color={color}
      onChangeComplete={(c) => setColor(c.hex)}
      disableAlpha
      styles={{
        default: {
          picker: {
            width: '100%',
            height: 340,
            boxShadow: 'none',
          },
          saturation: {
            width: '100%',
            paddingBottom: '56.25%',
          },
          hue: {
            height: 10,
            borderRadius: 2,
          }
        },
      }}
    />
     <Button
          variant="contained"
          onClick={e => {
          if (pickerType === "insole") {
            setInsoleValue(composeValue('custom', tempInsoleColor));
            setInsoleSelectOpen(false);
          } else {
            setAdditionValue(composeValue('custom', tempAdditionColor));
            setAdditionSelectOpen(false);
          }
          }}
          sx={{
            width: '30%',
            height: 30,
            align: 'center',
            backgroundColor: '#076596',
            color: '#fff',
            fontWeight: 300,
            '&:hover': {
              backgroundColor: '#0a6fb4',
            },
          }}
        >
          Apply
        </Button>
    </Box>
    );
  }
  return null;
};

const composeValue = (tab: string, color: string) => `${tab}:${color}`;

 const onTabChange = (
    e: React.SyntheticEvent,
    val: number,
    type: 'insole' | 'addition'
  ) => {
    e.stopPropagation();
    if (type === 'insole') {
      setInsoleTabIndex(val);
      if (val === 0) {
        setTempInsoleColor(parseValue(insoleValue).color || '');
      }
    } else {
      setAdditionTabIndex(val);
      if (val === 0) {
        setTempAdditionColor(parseValue(additionValue).color || '');
      }
    }
  };

const onTempColorChange = (color: string, type: 'insole' | 'addition') => {
    if (type === 'insole') setTempInsoleColor(color.toUpperCase());
    else setTempAdditionColor(color.toUpperCase());
  };

const onColorChange = (color: string, type: 'insole' | 'addition') => {
  const currentValue = type === 'insole' ? insoleValue : additionValue;
  const { tab } = parseValue(currentValue);

  if (type === 'insole') {
    setInsoleValue(composeValue(tab, color.toUpperCase()));
    setTempInsoleColor(color.toUpperCase()); 
  } else {
    setAdditionValue(composeValue(tab, color.toUpperCase()));
    setTempAdditionColor(color.toUpperCase());
  }
};

const renderColorValue = (val: string) => {
  const { tab, color } = parseValue(val);
  let displayText = '';

  if (tab === 'custom') displayText = color;
  else if (tab === 'web') displayText = getColorName(webColors, color) || 'Select Color';
  else if (tab === 'system') displayText = getColorName(systemColors, color) || 'Select Color';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        sx={{
          width: 50,
          height: 20,
          backgroundColor: color,
          border: '1px solid #ccc',
          borderRadius: '4px',
        }}
      />
      <Typography variant="body2">{displayText}</Typography>
    </Box>
  );
};

  return (
    <Modal open={isOpen} onClose={onClose} aria-labelledby="load-template-title" closeAfterTransition sx={{display: "flex", alignItems: "center", justifyContent: "center",}}>
      <Box
        sx={{
          bgcolor: isDarkMode ? '#333' : '#fff',
          color: isDarkMode ? '#fff' : '#000',
          borderRadius: 2,
          width: { xs: '95vw', md: '28vw' },
          maxHeight: { xs: '95vh', md: '80vh' }, 
          mx: 'auto',
          boxShadow: 24,
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          position: 'relative',
        }}
      >
        {/* Header */}
       <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #ccc' }}>
          <Typography variant="h6" sx={{ color: isDarkMode ? '#fff' : '#076596' }}>
            Save as Template
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon sx={{ color: isDarkMode ? '#fff' : '#000' }} />
          </IconButton>
        </Box>

        {/* content */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            p: 3,
          }}
        >
          {/* Template Title */}
          <TextField
            label="Template Title"
            value={templateTitle}
            onChange={(e) => setTemplateTitle(e.target.value)}
            fullWidth
            required
            variant="outlined"
            size="small"
            sx={{ mb: 2,
              '& .MuiInputBase-input': {
              fontSize: '0.95rem',
              color: isDarkMode ? '#fff' : '#000'
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.95rem', 
                color: isDarkMode ? '#fff' : '#000'
              },
            }}
          />

          {/* Category Selector */}
          <Autocomplete
            freeSolo
            options={categoryOptions}
            value={category}
            onChange={(event, newValue) => {
              setCategory(newValue);
            }}
            onInputChange={(event, newInputValue) => {
              setCategory(newInputValue);
            }}
            slotProps={{
            paper: {
              sx: {
                fontSize: '0.95rem',
              },
            },
            popper: {
              sx: {
                '& .MuiAutocomplete-option': {
                  fontSize: '0.95rem',
                  padding: '4px 8px',
                },
              },
            },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Category"
                variant="outlined"
                size="small"
                sx={{ mb: 2, 
                  '& .MuiInputBase-input': {
                  fontSize: '0.95rem',
                  color: isDarkMode ? '#fff' : '#000'
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.95rem', 
                  color: isDarkMode ? '#fff' : '#000'
                },
                }}
              />
            )}
          />

          {/* Mini Viewer */}
          <Box sx={{ mb: 1.5 }}>
            <MiniViewer3D ref={miniViewerRef} />
          </Box>

          {/* Insole Selector */}
          <Box sx={{ mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontSize: '0.95rem' }}>
            Insoles Color
          </Typography>
          <Select
            ref={insoleSelectRef}
            open={insoleSelectOpen}
            onOpen={() => setInsoleSelectOpen(true)}
            onClose={() => {}} 
            value={insoleTabIndex}
            fullWidth
            size="small"
            renderValue={() => renderColorValue(insoleValue)} 
            MenuProps={{
              PaperProps: {
                ref: insoleMenuRef,
                onClick: e => e.stopPropagation(),
              },
            }}
            sx={{'& .MuiInputBase-input': {
                  color: isDarkMode ? '#fff' : '#000',}}}
            >
            <MenuItem disableRipple disableTouchRipple sx={{ cursor: 'default', pt: 0, pb: 0, }}  
            >
              <Tabs
                value={insoleTabIndex}
                onChange={(e, val) => onTabChange(e, val, "insole")}
                variant="fullWidth"
              >
                <Tab label="Custom" value={0} />
                <Tab label="Web" value={1} />
                <Tab label="System" value={2} />
              </Tabs>
            </MenuItem>

            <MenuItem disableRipple disableTouchRipple sx={{ cursor: 'default', display: 'flex', flexDirection: 'column', gap: 0.5 }} 
            >
              {insoleTabIndex === 0 && renderColorPicker(0, tempInsoleColor, (color) => onTempColorChange(color.toUpperCase(), "insole"), 'insole')}
              {insoleTabIndex === 1 && (
              <Box
                sx={{
                  maxWidth: 410,
                  height: 370,
                  overflowY: 'auto',
                  pr: 1,
                  width: '100%',
                }}
              >
                {webColors.map(c => (
                  <Box
                    key={c.value}
                    onClick={() => {
                      setInsoleValue(composeValue("web", c.value.toUpperCase()));
                      setInsoleSelectOpen(false); 
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.5,
                      px: 1,
                      cursor: 'pointer',
                      borderRadius: 1,
                      width: '100%',
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      backgroundColor: parseValue(insoleValue).color === c.value ? 'rgba(7,101,150,0.2)' : 'transparent',
                      '&:hover': { backgroundColor: 'rgba(7,101,150,0.3)' },
                    }}
                  >
                    <Box sx={{ width: 48, height: 24, backgroundColor: c.value, border: '1px solid #ccc', borderRadius: "4px", }} />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>{c.name}</Typography>
                  </Box>
                ))}
              </Box>
              )}
              {insoleTabIndex === 2 && (
              <Box
                sx={{
                  maxWidth: 410,
                  height: 370,
                  overflowY: 'auto',
                  pr: 1,
                  width: '100%',
                }}
              >
                {systemColors.map(c => (
                  <Box
                    key={c.value}
                    onClick={() => {
                      onColorChange(c.value, 'insole');
                      setInsoleSelectOpen(false); 
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.5,
                      px: 1,
                      cursor: 'pointer',
                      borderRadius: 1,
                      width: '100%',
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      backgroundColor: parseValue(insoleValue).color === c.value ? 'rgba(7,101,150,0.2)' : 'transparent',
                      '&:hover': { backgroundColor: 'rgba(7,101,150,0.3)' },
                    }}
                  >
                    <Box sx={{ width: 48, height: 24, backgroundColor: c.value, border: '1px solid #ccc', borderRadius: "4px", }} />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>{c.name}</Typography>
                  </Box>
                ))}
              </Box>
            )}
            </MenuItem>
          </Select>
          </Box>
          

        {/* Addition Selector */}
        <Box>
          <Typography variant="subtitle1" sx={{ fontSize: '0.95rem' }}>
            Additions Color
          </Typography>
          <Select
            ref={additionSelectRef}
            open={additionSelectOpen}
            onOpen={() => setAdditionSelectOpen(true)}
            onClose={() => {}} 
            value={additionTabIndex}
            fullWidth
            size="small"
            renderValue={() => renderColorValue(additionValue)}
            MenuProps={{
              PaperProps: {
                ref: additionMenuRef,
                onClick: e => e.stopPropagation(),
              },
            }}
            sx={{'& .MuiInputBase-input': {
                  color: isDarkMode ? '#fff' : '#000',}}}
          >
            <MenuItem
              disableRipple
              disableTouchRipple
              sx={{ cursor: 'default', pt: 0, pb: 0 }}
              onClick={(e) => { e.stopPropagation(); }}
            >
              <Tabs
                value={additionTabIndex}
                onChange={(e, val) => onTabChange(e, val, "addition")}
                variant="fullWidth"
              >
                <Tab label="Custom" value={0} />
                <Tab label="Web" value={1} />
                <Tab label="System" value={2} />
              </Tabs>
            </MenuItem>

            <MenuItem
              disableRipple
              disableTouchRipple
              sx={{ cursor: 'default', display: 'flex', flexDirection: 'column', gap: 0.5 }}
              onClick={(e) => {
                if (parseValue(additionValue).tab === "custom") e.stopPropagation();
              }}
              >
              {additionTabIndex === 0 && renderColorPicker(0, tempAdditionColor, (color) => onTempColorChange(color.toUpperCase(), "addition"), 'addition')}
              {additionTabIndex === 1 && (
                <Box
                  sx={{
                    maxWidth: 410,
                    height: 370,
                    overflowY: "auto",
                    pr: 1,
                    width: "100%",
                  }}
                >
                  {webColors.map((c) => (
                    <Box
                      key={c.value}
                      onClick={() => {
                        setAdditionValue(composeValue("web", c.value.toUpperCase()));
                        setAdditionSelectOpen(false);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        py: 0.5,
                        px: 1,
                        cursor: "pointer",
                        borderRadius: 1,
                        width: "100%",
                        justifyContent: "flex-start",
                        textAlign: "left",
                        backgroundColor:
                          parseValue(additionValue).color === c.value
                            ? "rgba(7,101,150,0.2)"
                            : "transparent",
                        "&:hover": { backgroundColor: "rgba(7,101,150,0.3)" },
                      }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 24,
                          backgroundColor: c.value,
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                        }}
                      />
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {c.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                )}
                {additionTabIndex === 2 && (
                <Box
                  sx={{
                    maxWidth: 410,
                    height: 370,
                    overflowY: "auto",
                    pr: 1,
                    width: "100%",
                  }}
                >
                  {systemColors.map((c) => (
                    <Box
                      key={c.value}
                      onClick={() => {
                        onColorChange(c.value.toUpperCase(), "addition");
                        setAdditionSelectOpen(false);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        py: 0.5,
                        px: 1,
                        cursor: "pointer",
                        borderRadius: 1,
                        width: "100%",
                        justifyContent: "flex-start",
                        textAlign: "left",
                        backgroundColor:
                          parseValue(additionValue).color === c.value
                            ? "rgba(7,101,150,0.2)"
                            : "transparent",
                        "&:hover": { backgroundColor: "rgba(7,101,150,0.3)" },
                      }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 24,
                          backgroundColor: c.value,
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                        }}
                      />
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {c.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </MenuItem>
          </Select>
        </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: '1px solid #ccc', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{ color: isDarkMode ? '#fff' : '#000', 
              borderColor: '#aaa',
              width: '80px', 
              fontWeight: 500, }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!templateTitle.trim()}
            sx={{ color: '#fff', 
                borderColor: '#aaa', 
                backgroundColor: '#076596',
                width: '80px', 
                fontWeight: 500,
                px: 4,
                '&:hover': { backgroundColor: '#0a6fb4' } }}
            >
            Save
            </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SaveTemplateModal;
